import { useState } from "react";

import { t } from "../contract/strings";
import { themes, useTheme } from "../state/theme";
import { useApp } from "../state/store";
import { setEmailMeetings } from "../../lib/api.js";
import {
  getShareUsageStatsPreference,
  isGlobalPrivacyControlEnabled,
  setShareUsageStatsEnabled,
} from "../analytics";
import { captureAnalytics } from "../analytics";
import { MicIcon, ThemeDial } from "../ui";
import { Screen } from "./screen";

export function Settings() {
  const selected = useTheme((state) => state.theme);
  const selectTheme = useTheme((state) => state.selectTheme);
  const account = useApp((state) => state.account);
  const setAccount = useApp((state) => state.setAccount);
  const [shareUsageStats, setShareUsageStats] = useState(getShareUsageStatsPreference);
  const globalPrivacyControl = isGlobalPrivacyControlEnabled();
  const active = themes.find((theme) => theme.id === selected)!;
  // The switch's own state while the write is in the air. Settings is signed-in only (the
  // route guard); the switch is the account's, and email-meetings-off-by-link people
  // return here to turn it back on (#864).
  const [emailBusy, setEmailBusy] = useState(false);
  // Absent on an older server reads as on, the server's own default (#864).
  const emailMeetings = account ? (account.email_meetings ?? true) : false;

  return (
    <Screen name="settings">
      <h2>{t("settings.heading")}</h2>
      <fieldset className="m-0 p-0 border-none min-w-0">
        <legend className="p-0 text-who font-semibold">{t("settings.theme")}</legend>

        {/* The card shows the palette, the dial to its right picks it: one row at every
            width, the card taking the wider half because it carries the words. */}
        <div className="theme-stage">
          <div
            className="theme-preview rounded-panel border border-line bg-bg"
            aria-label={t("settings.preview_aria")}
          >
            {/* The theme's name is in the dial's own accent bar now, so the card keeps
                only its title: on a phone the two never fit on one line. */}
            <div className="text-note text-ink-soft">
              <strong className="font-code block">{t("settings.live_preview")}</strong>
            </div>
            <div className="mt-3 flex items-center gap-2 p-3 rounded-panel bg-accent text-accent-ink text-note font-semibold">
              <MicIcon className="size-icon" />
              <span>{t("settings.start_conversation")}</span>
            </div>
            <div className="flex items-center justify-between gap-2 mt-2 p-3 rounded-panel border border-line bg-surface text-note">
              <span>{t("settings.project_checkin")}</span>
              <span className="text-ink-soft">{t("settings.today")}</span>
            </div>
          </div>

          <ThemeDial
            themes={themes}
            selected={selected}
            onSelect={(theme) => {
              captureAnalytics("theme_selected", { theme });
              selectTheme(theme);
            }}
          />
        </div>
      </fieldset>
      <p className="sr-only" role="status" aria-live="polite">
        {t("settings.current_theme", { name: active.name })}
      </p>

      <fieldset className="mt-8 p-0 border-none min-w-0">
        <legend className="p-0 text-who font-semibold">{t("settings.privacy")}</legend>
        <label className="mt-3 flex items-start gap-3">
          <input
            type="checkbox"
            checked={shareUsageStats && !globalPrivacyControl}
            disabled={globalPrivacyControl}
            onChange={(event) => {
              const enabled = event.currentTarget.checked;
              setShareUsageStats(enabled);
              setShareUsageStatsEnabled(enabled);
            }}
            className="mt-1 accent-accent"
          />
          <span>
            <span className="block font-medium">{t("settings.share_stats")}</span>
            <span className="block mt-1 text-note text-ink-soft">
              {t("settings.share_stats_hint")}
            </span>
          </span>
        </label>
        {globalPrivacyControl && (
          <p className="mt-2 text-note text-ink-soft" role="status">
            {t("settings.gpc")}
          </p>
        )}
      </fieldset>
      <fieldset className="mt-8 p-0 border-none min-w-0">
        <legend className="p-0 text-who font-semibold">{t("settings.notifications")}</legend>
        {account ? (
          <label className="mt-3 flex items-start gap-3">
            <input
              type="checkbox"
              checked={emailMeetings}
              disabled={emailBusy}
              onChange={(event) => {
                const enabled = event.currentTarget.checked;
                setEmailBusy(true);
                setEmailMeetings(enabled)
                  .then((updated) => {
                    captureAnalytics("email_meetings_toggled", { state: enabled ? "on" : "off" });
                    setAccount(updated);
                  })
                  .catch(() => {
                    // The switch reads what the server last said; a failed write leaves
                    // it standing rather than pretending, and the checkbox snaps back.
                  })
                  .finally(() => setEmailBusy(false));
              }}
              className="mt-1 accent-accent"
            />
            <span>
              <span className="block font-medium">{t("settings.email_meetings")}</span>
              <span className="block mt-1 text-note text-ink-soft">
                {t("settings.email_meetings_hint")}
              </span>
            </span>
          </label>
        ) : null}
      </fieldset>
    </Screen>
  );
}
